import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Mail } from 'lucide-react';
import { useEditableSettings } from '@/hooks/useEditableSettings';
import { EditableText } from '@/components/EditableText';
import { SiteNav } from '@/components/SiteNav';
import { API } from '@/App';

const Contact = () => {
  const { settings, isAdmin, updateField, updateLabel, setUser } = useEditableSettings();
  const [form, setForm] = useState({ email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem('session_token');
    axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMessages(r.data))
      .catch(() => {});
  }, [isAdmin]);

  if (!settings) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-pulse text-[11px] tracking-[0.3em] uppercase text-[#525252]">Loading</div></div>;
  }

  const L = settings.labels || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) {
      toast.error('Email and message are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(L.contact_success || 'Message sent. Thank you.');
      setForm({ email: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to send. Please try again.');
    }
    setSubmitting(false);
  };

  const deleteMessage = async (id) => {
    const token = localStorage.getItem('session_token');
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(messages.filter(m => m.message_id !== id));
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="contact-page">
      <SiteNav settings={settings} isAdmin={isAdmin} setUser={setUser} updateField={updateField} updateLabel={updateLabel} active="contact" />

      <div className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-24">
        <div className="max-w-xl mx-auto">
          <EditableText
            as="h2"
            value={L.contact_title ?? 'Contact'}
            onChange={(v) => updateLabel('contact_title', v)}
            editable={isAdmin}
            className="text-[11px] tracking-[0.2em] uppercase font-bold mb-4 block"
            testId="contact-title"
          />

          <EditableText
            value={L.contact_subtitle ?? 'Press, collaborations, bookings, and quiet correspondence.'}
            onChange={(v) => updateLabel('contact_subtitle', v)}
            editable={isAdmin}
            multiline
            className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-[#737373] font-light leading-[2] mb-14 sm:mb-20 block max-w-md"
            testId="contact-subtitle"
          />

          <form onSubmit={submit} className="space-y-8">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#525252] mb-2 block">
                {L.contact_email_label || 'Email'}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full bg-transparent border-0 border-b border-[#262626] focus:border-black focus:outline-none py-2 text-sm"
                placeholder="you@example.com"
                data-testid="contact-input-email"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#525252] mb-2 block">
                {L.contact_subject_label || 'Subject'}
              </label>
              <input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[#262626] focus:border-black focus:outline-none py-2 text-sm"
                placeholder="A line, optional"
                data-testid="contact-input-subject"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#525252] mb-2 block">
                {L.contact_message_label || 'Message'}
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={6}
                className="w-full bg-transparent border border-[#262626] focus:border-black focus:outline-none p-3 text-sm leading-relaxed"
                placeholder="—"
                data-testid="contact-input-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="bg-black text-white px-6 py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#262626] transition-colors disabled:opacity-60"
              data-testid="contact-submit"
            >
              {submitting ? (L.contact_sending || 'Sending…') : (L.contact_send_btn || 'Send')}
            </button>
          </form>

          {isAdmin && (
            <div className="mt-20 border-t border-[#E5E5E5] pt-10">
              <h3 className="text-[11px] tracking-[0.2em] uppercase font-bold mb-6 flex items-center gap-2">
                <Mail className="w-4 h-4" strokeWidth={1.5} /> Inbox <span className="text-[#A3A3A3] font-normal">({messages.length})</span>
              </h3>
              {messages.length === 0 ? (
                <p className="text-[#A3A3A3] text-sm">No messages yet.</p>
              ) : (
                <div className="space-y-4">
                  {messages.map((m) => (
                    <div key={m.message_id} className="bg-white border border-[#E5E5E5] p-5 group" data-testid={`message-${m.message_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="text-[12px] tracking-[0.05em] text-black hover:underline break-all">{m.email}</a>
                          <p className="text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mt-1">{new Date(m.created_at).toLocaleString()}</p>
                        </div>
                        <button onClick={() => deleteMessage(m.message_id)} className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-black transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                      <p className="text-[13px] font-semibold mb-2">{m.subject}</p>
                      <p className="text-[13px] leading-[1.85] text-[#262626] whitespace-pre-line">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
